import React, { ReactNode, forwardRef } from 'react';
import cn from 'classnames';
import styles from './extended-text-fields.module.scss';
import { Label, Variant } from '../label/label';
import { TextFieldProps } from '../main';
import { HelperText } from '../helper-text/helper-text';
import { ClearProps, TextFieldWithClear } from './with-clear-button';
import { TextFieldWithIcon, IconProps } from './with-icon';
import { getBaseCompByType } from './helpers';

type CombinedTextFieldProps = TextFieldProps & ClearProps & IconProps;

const extendedComponents = {
  TextFieldWithClear,
  TextFieldWithIcon,
};

type ExtendedComponents = keyof typeof extendedComponents;

const getComponent = (type: string, extendedComp?: ExtendedComponents) => {
  if (extendedComp) {
    return extendedComponents[extendedComp];
  }
  return getBaseCompByType(type);
};

export type LabelTextFieldProps = CombinedTextFieldProps & {
  name: string;
  label?: string | ReactNode;
  variant?: Variant;
  isHelperTextHidden?: boolean;
  helperText?: string;
  extendedComponent?: ExtendedComponents;
};

export const TextFieldWithLabel = forwardRef<
  HTMLInputElement,
  LabelTextFieldProps
>(
  (
    {
      name,
      helperText,
      label,
      children,
      onMouseDown,
      className,
      variant = 'regular',
      flexedStyle = true,
      type = 'text',
      extendedComponent,
      ...textFieldProps
    },
    ref,
  ) => {
    const Component = getComponent(type, extendedComponent);
    // Note* avoid point-events: none; to not break the cursor: not-allowed; effect
    const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (event) =>
      !textFieldProps.disabled && onMouseDown?.(event);

    return (
      <div
        className={cn(flexedStyle && styles.flexed, className)}
        onMouseDown={handleMouseDown}
      >
        {label && (
          <Label
            variant={variant}
            label={label}
            name={name}
            disabled={textFieldProps.disabled}
            flexedStyle={flexedStyle}
          />
        )}
        <Component
          {...textFieldProps}
          ref={ref}
          id={name}
          flexedStyle={flexedStyle}
          type={type}
          name={name}
          {...(helperText && { 'aria-describedby': `helper-text-${name}` })}
        >
          {children}
        </Component>
        {helperText && (
          <HelperText
            name={name}
            text={helperText}
            hasError={textFieldProps.hasError}
          />
        )}
      </div>
    );
  },
);
